import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import appHelpers from '@utils/app-urls';
import store from 'store';
import { setConnectionState } from 'store/signalr/signalr-slice';
import { lowerCase } from './casing';

export type ConnectionState = 'initial' | 'connected' | 'disconnected' | 'connecting' | 'disconnecting' | 'reconnecting';

export class SignalRService {
    private signalrConnection: HubConnection;
    private subscribedEvents = new Map<string, {
        handler: (message: any) => void,
        reconnectHandler?: () => void,
    }>();
    private signalrConnected = false;
    private signalrConnectionPromise: Promise<void> | undefined = undefined;
    private previousToken: string | undefined = undefined;
    public onConnectionStateChange: (state: ConnectionState) => void = () => { };

    constructor() {
        this.signalrConnection = this.createConnection();
    }

    private publishConnectionState() {
        this.onConnectionStateChange(lowerCase(this.signalrConnection.state));
    }

    async setAuthenticationToken(token?: string) {
        if (this.signalrConnectionPromise != undefined) {
            await this.signalrConnectionPromise;
        }

        if (this.previousToken == token)
            return;

        this.previousToken = token;
        this.signalrConnectionPromise = this.reconnectWithToken(token);
        await this.signalrConnectionPromise;
    }

    async subscribeEvent<T extends Object>(eventName: string, handler: (message: T) => void, onReconnect?: () => void) {
        await this.ensureSignalrConnection();

        if (this.subscribedEvents.has(eventName)) {
            throw new Error(`Event with name ${eventName} is already subscribed`);
        }

        this.signalrConnection.on(eventName, (data: T) => handler(data));
        this.subscribedEvents.set(eventName, { handler, reconnectHandler: onReconnect });

        await this.signalrConnection.send('SubscribeEvent', eventName);
        console.log(`Subscribed event ${eventName}`);
    }

    async unsubscribeEvent(eventName: string) {
        this.signalrConnection.off(eventName);
        const eventExisted = this.subscribedEvents.delete(eventName);

        if (eventExisted) {
            await this.signalrConnection.send('UnsubscribeEvent', eventName);
            console.log(`Unsubscribed event ${eventName}`);
        }
    }

    private createConnection(token?: string): HubConnection {
        console.log('Creating signalr connection');
        const headers = token == undefined ? undefined : { authorization: `Bearer ${token}` };

        const signalrConnection = new HubConnectionBuilder()
            .withUrl(`${appHelpers.getEndpointUrl()}/events`, { headers, accessTokenFactory: () => token ?? '' })
            .configureLogging(LogLevel.Information)
            .withAutomaticReconnect()
            // .withServerTimeout(10 * 1000)
            // .withKeepAliveInterval(10 * 1000)
            .build();

        signalrConnection.onreconnected(async () => {
            console.log('SignalR reconnected');
            this.publishConnectionState();
            await this.handleReconnect();
        });

        signalrConnection.onreconnecting(async () => {
            console.log('SignalR reconnecting');
            this.publishConnectionState();
        });

        signalrConnection.onclose(async () => {
            console.log('SignalR connection closed');
            this.publishConnectionState();
        });


        return signalrConnection;
    }

    private async handleReconnect() {
        for (const [eventName, { reconnectHandler }] of this.subscribedEvents) {
            await this.signalrConnection.send('SubscribeEvent', eventName);
            if (reconnectHandler) {
                reconnectHandler();
            }
        }
        console.log(`${this.subscribedEvents.size} events resubscribed`);
    }

    private async reconnectWithToken(token?: string) {
        const wasConnected = this.signalrConnection.state != HubConnectionState.Disconnected && this.signalrConnection.state != HubConnectionState.Disconnecting;
        console.log(`Handling authentication change for connection state ${wasConnected}`);

        if (wasConnected) {
            console.log('Stopping singnalr connection');
            await this.signalrConnection.stop();
        }

        this.signalrConnection = this.createConnection(token);
        if (wasConnected) {
            console.log('Restarting signalr connection');
            await this.signalrConnection.start();
            this.publishConnectionState();
            await this.handleReconnect();
        }
    }

    private async ensureSignalrConnection() {
        if (this.signalrConnectionPromise != undefined) {
            await this.signalrConnectionPromise;
        }

        if (this.signalrConnected)
            return;

        try {
            this.signalrConnectionPromise = this.signalrConnection.start();
            await this.signalrConnectionPromise;
            this.signalrConnected = true;
            this.publishConnectionState();

            console.log('SignalR connected');
        } catch (error) {
            console.error('SignalR connection error:', error);
        }
    }
}

export const signalRService = new SignalRService();

signalRService.onConnectionStateChange = (state) => {
    store.dispatch(setConnectionState(state));
}